import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { affectBlockedPlace } from '../../../../api/blocked-places/affect-blocked-place'
import { deleteBlockedPlace } from '../../../../api/blocked-places/delete-blocked-place'
import { getCenter } from '../../../../api/centers/get-center'
import {
  EnrollmentCenter,
  EnrollmentCenterFilters
} from '../../../../lib/types/models/enrollment-center/enrollment-center'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import DynamicDataTable from '../../../elements/dynamic-table'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = {
  centerId: number
  enrollmentId: number
  blockedPlaceId: number
  className?: string
  onSucceed: () => void
  onCancel: () => void
}

export const AssociateBlockedPlace = ({
  className,
  blockedPlaceId,
  centerId,
  enrollmentId,
  onSucceed,
  onCancel
}: Props) => {
  const { data } = useQuery({
    queryKey: ['centers', centerId],
    queryFn: () => getCenter(centerId)
  })

  const { mutateAsync: affectBlockedPlaceMutation } = useMutation({
    mutationFn: affectBlockedPlace,
    onSuccess: onSucceed,
    onError: showAxiosError
  })

  const { mutateAsync: deleteBlockedPlaceMutation } = useMutation({
    mutationFn: deleteBlockedPlace,
    onSuccess: onSucceed,
    onError: showAxiosError
  })

  const columns: Column<EnrollmentCenter>[] = useMemo(
    () => [
      {
        accessorKey: 'enrollment.childLastName',
        header: 'NOM',
        cell: ({ row }) => {
          const lastName = row.original.enrollment.childLastName
          return <div className="uppercase text-balance py-1">{lastName}</div>
        }
      },
      {
        accessorKey: 'enrollment.childFirstName',
        header: 'Prénom',
        cell: ({ row }) => {
          const firstName = row.original.enrollment.childFirstName
          return <div className="capitalize text-balance py-1">{firstName}</div>
        }
      },
      {
        accessorKey: 'enrollment.declaredFriends',
        header: 'Amis déclarés',
        cell: ({ row }) => {
          const declaredFriends = row.original.enrollment.declaredFriends
          if (!declaredFriends || declaredFriends.length === 0) return '-'
          return (
            <div className="capitalize text-balance py-1">
              {declaredFriends
                ?.map((friend) => toFullName(friend.firstName, friend.lastName))
                .join(', ')}
            </div>
          )
        }
      },
      {
        accessorKey: 'id',
        header: '',
        cell: ({ row }) => (
          <Button.Loading
            size="small"
            label="Associer"
            onClick={async () => {
              await affectBlockedPlaceMutation({
                centerId,
                blockedPlaceId,
                enrollmentId: row.original.enrollment.id
              })
            }}
          />
        )
      }
    ],
    [data]
  )

  if (!data) return <Loader center />

  return (
    <div>
      <div className={className}>
        <DynamicDataTable<EnrollmentCenter, EnrollmentCenterFilters>
          columns={columns}
          paginated={false}
          filters={{ excludedIds: [enrollmentId] }}
          selectable={false}
          permissions={{ delete: false }}
          headerType="light"
          endpoint={constructApiUrl(API.centers.getEnrollments.url, { id: centerId })}
        />
        <div className="h-3" />
        <div className="flex justify-center items-center">
          <Badge variant="grey" text="-Ou-" />
        </div>
        <div className="h-3" />
        <div className="p-2 flex justify-between items-center border border-grey-light rounded text-greey">
          <Typography variant="tag">Débloquer la place</Typography>
          <Button.Loading
            label="Débloquer"
            variant="destructive"
            onClick={async () => {
              await deleteBlockedPlaceMutation({ centerId, blockedPlaceId })
            }}
          />
        </div>
        <div className="h-3" />
      </div>
      <ValidationButtons hasBorderTop buttonsConfig={modalButtonConfig({ onCancel })} />
    </div>
  )
}
